import {Company} from "@/models/company";
import {TicketAssignedAdmin} from "@/models/ticket-admin";
import {TicketMessage} from "@/models/ticket-message";
import {TicketUnreadAdmin} from "@/models/ticket-unread-admin";
import {User} from "@/models/user";
import {Department} from "@/utils/department";
import {TicketStatus} from "@/utils/ticket-status";
import {AuthSocketService} from "@/services/auth-socket-service";
import {TokenStore} from "@/utils/token-store";
import {TicketUnreadUser} from "./ticket-unread-user";
import {Admin} from "./admin";
import {TicketAdminPin} from "./ticket-admin-pin";
import {AuthService} from "@/services/auth-service";
import {TicketAdminBookmark} from "./ticket-admin-bookmark";
import {icon as faIcon} from "@fortawesome/fontawesome-svg-core";

export class Ticket {
	id: number;
	clientId: number;
	timeCreated: Date;
	timeLastMessage: Date;
	timeSheduled: Date;
	departmentId: Department;
	dossierId: number;
	dossierName: string;
	subject: string;
	statusId: TicketStatus;
	client?: User;
	company?: Company;
	visibleForClient: boolean;
	priority: number;
	messages: TicketMessage[] = [];
	assignedAdmins: TicketAssignedAdmin[] = [];
	unreadAdmins: TicketUnreadAdmin[] = [];
	unreadUsers: TicketUnreadUser[] = [];
	openedAdmins: Admin[] = [];
	adminPins: TicketAdminPin[] = [];
	adminBookmarks: TicketAdminBookmark[] = [];
	ticketStatus: string;

	constructor(data: any) {
		this.id = data.id;
		this.clientId = data.clientId;
		this.timeCreated = new Date(data.timeCreated);
		this.timeSheduled = new Date(data.timeSheduled);
		this.timeLastMessage = new Date(data.timeLastMessage);
		this.departmentId = data.departmentId;
		this.dossierId = data.dossierId || 0;
		this.dossierName = data.dossierName || "";
		this.subject = data.subject;
		this.statusId = data.statusId;
		this.priority = data.priority;
		this.visibleForClient = data.visibleForClient;
		if (data.client) {
			this.client = new User(data.client);
			this.company = new Company(data.client.Company);
		}
		for (let message of data.messages || []) {
			this.messages.push(new TicketMessage(message));
			this.sortMessages();
		}
		for (let admin of data.assignedAdmins || []) {
			this.assignedAdmins.push(new TicketAssignedAdmin(admin));
		}
		for (let user of data.unreadUsers || []) {
			this.unreadUsers.push(new TicketUnreadUser(user));
		}
		for (let admin of data.unreadAdmins || []) {
			this.unreadAdmins.push(new TicketUnreadAdmin(admin));
		}
		this.isTicketRead = this.isTicketRead.bind(this);
		if (data.adminPins) {
			this.adminPins = data.adminPins.map((p: any) => new TicketAdminPin(p));
		}
		if (data.adminBookmarks) {
			this.adminBookmarks = data.adminBookmarks.map((b: any) => new TicketAdminBookmark(b));
		}
		this.ticketStatus = this.getTicketStatus();
	}

	getTimeCreatedString(): string {
		return `${this.timeCreated.getDate()}/${this.timeCreated.getMonth() + 1}/${this.timeCreated.getFullYear()}`;
	}

	getTimeSheduled(): string {
		return `${this.timeSheduled.getDate()}/${this.timeSheduled.getMonth() + 1}/${this.timeSheduled.getFullYear()}`;
	}

	isTicketSheduled(): boolean {
		return this.timeSheduled.getFullYear() > 1970;
	}

	getTicketStatus(): string {
		let s = "<div class='icon-box'>";

		if (this.isPinned()) {
			s += faIcon({prefix: "fas", iconName: "thumbtack"}).html[0];
		}

		if (!this.isTicketRead()) {
			s += "<div class='bullit'></div>";
		}

		s += "</div>";

		return s;
	}

	isTicketRead(): boolean {
		for (let admin of this.unreadAdmins) {
			if (admin.adminId == TokenStore.userId) {
				return false;
			}
		}
		return true;
	}

	sortMessages() {
		this.messages = this.messages.sort((a, b) => {
			return a.timeCreated.getTime() - b.timeCreated.getTime();
		});
	}

	// eslint-disable-next-line max-lines-per-function
	registerSocketEvents() {
		this.deregisterSocketEvents();
		this.registerSocketEventsReadAndUnread();
		AuthSocketService.on(`/ticket/${this.id}/update`, (data: any) => {
			this.clientId = data.clientId;
			this.departmentId = data.departmentId;
			this.subject = data.subject;
			this.statusId = data.statusId;
			this.visibleForClient = data.visibleForClient;
			this.timeLastMessage = new Date(data.timeLastMessage);
		}, this);
		AuthSocketService.on(`/ticket/${this.id}/message`, (data: any) => {
			let message = new TicketMessage(data);
			message.registerSocketEvents();
			this.messages.push(message);
			this.sortMessages();
		}, this);
		AuthSocketService.on(`/ticket/${this.id}/message-deleted`, (data: number) => {
			let i = this.messages.findIndex(m => m.id == data);
			if (i != -1) {
				this.messages.splice(i, 1);
			}
		});
		AuthSocketService.on(`/ticket/${this.id}/admin-assigned`, (data: any) => {
			let ticketAdmin = new TicketAssignedAdmin(data);
			this.assignedAdmins.push(ticketAdmin);
		}, this);
		AuthSocketService.on(`/ticket/${this.id}/admin-removed`, (data: any) => {
			let ticketAdmin = new TicketAssignedAdmin(data);
			let i = this.assignedAdmins.findIndex(t => t.adminId == ticketAdmin.adminId);
			if (i != -1) {
				this.assignedAdmins.splice(i, 1);
			}
		}, this);
		AuthSocketService.on(`/ticket/${this.id}/join`, (data: any) => {
			let admin = new Admin(data);
			if (this.openedAdmins.findIndex(a => a.id == admin.id) != -1) return;
			this.openedAdmins.push(admin);
		});
		AuthSocketService.on(`/ticket/${this.id}/leave`, (data: any) => {
			let admin = new Admin(data);
			for (let i = this.openedAdmins.length - 1; i >= 0; i--) {
				if (this.openedAdmins[i].id == admin.id) {
					this.openedAdmins.splice(i, 1);
				}
			}
		});
		this.messages.forEach(m => m.registerSocketEvents());
	}

	join() {
		AuthSocketService.emit("/ticket/join", this.id);
	}

	leave() {
		AuthSocketService.emit("/ticket/leave", this.id);
	}

	registerSocketEventsReadAndUnread() {
		AuthSocketService.on(`/ticket/${this.id}/unread`, (data: any) => {
			this.unreadAdmins.push(new TicketUnreadAdmin(data));
		}, this);
		AuthSocketService.on(`/ticket/${this.id}/user_unread`, (data: any) => {
			this.unreadUsers.push(new TicketUnreadUser(data));
		});
		AuthSocketService.on(`/ticket/${this.id}/user_read`, (data: any) => {
			let read = new TicketUnreadUser(data);
			for (let i = this.unreadUsers.length - 1; i >= 0; i--) {
				if (read.UserID == this.unreadUsers[i].UserID) {
					this.unreadUsers.splice(i, 1);
				}
			}
		});
	}

	deregisterSocketEvents() {
		AuthSocketService.offAll(this);
		this.messages.forEach(m => m.deregisterSocketEvents());
	}

	isPinned(): boolean {
		return this.adminPins.find(p => {
			if (!AuthService.user) return false;
			return p.adminId == AuthService.user.id;
		}) != null;
	}

	isBookmarked(): boolean {
		return this.adminBookmarks.find(b => {
			if (!AuthService.user) return false;
			return b.adminId == AuthService.user.id;
		}) != null;
	}

	public getBrowseClass(): string[] {


		if (this.company && this.company.isDemo) {
			return ["demo"];
		}
		if (this.company) {
			if (this.company.servicePackExpirationDate && this.company.servicePackExpirationDate.getTime() <= new Date().getTime()) {
				return ["expired"];
			}
		}

		return [];
	}
}